import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { selectAuth } from "../auth/authSlice";
import { GROUPS, hasAnyGroup } from "../auth/permissions";
import { getApiBaseUrl } from "../../shared/api";
import { ICONS } from "../../shared/icons";
import "./AssistantWidget.css";
import { getAccessToken } from "../../shared/tokenStorage";
import {
  clearError,
  deleteAssistantSession,
  fetchAssistantHistory,
  fetchAssistantSuggestions,
  historyCleared,
  pendingQuestionCleared,
  pendingQuestionQueued,
  resumeLatestSession,
  revealNextChars,
  selectAssistant,
  setDraft,
  toggleOpen,
  togglePanelSize,
  userMessageSent,
} from "./assistantSlice";
import { connectAssistantSocket } from "./assistantSocket";
import * as assistantActions from "./assistantSlice";
import AssistantChart, { AssistantDebtTable } from "./AssistantChart";
import AssistantErrorBoundary from "./AssistantErrorBoundary";
import AssistantLinkCard, { extractInternalLinks } from "./AssistantLinkCard";
import AssistantTeaser from "./AssistantTeaser";
import ConfirmModal from "../../components/ConfirmModal";
import formatAssistantText from "./formatAssistantText";
import { REVEAL_INTERVAL_MS, revealChunkSize } from "./typing";
import { dismissTeaserForToday, isTeaserDismissedToday } from "./teaserStorage";

// Тизер не должен всплывать поверх ещё грузящейся страницы, но и висеть весь день тоже не должен.
const TEASER_DELAY_MS = 2500;
const TEASER_AUTO_HIDE_MS = 12000;

let socketHandle = null;

// Kill switch: set REACT_APP_ASSISTANT_ENABLED=false (env or .env.local) to hide the
// widget entirely without a code change/redeploy of the rest of the app.
const ASSISTANT_ENABLED = process.env.REACT_APP_ASSISTANT_ENABLED !== "false";

// Темп печати вынесен в ./typing.js — он адаптивный (ускоряется при большом буфере),
// поэтому длинные ответы больше не рисуются по 15 секунд.

// Deep-links to render for a message: structured open_page links (gated by role) plus any
// markdown links the model wrote inline in its text (fallback when it doesn't call open_page).
// Deduped by target path so the same page isn't shown twice.
function buildMessageLinks(m, user) {
  const structured = (m.links || []).filter((l) => !l.groups || hasAnyGroup(user, l.groups));
  const fromText = m.role === "assistant" ? extractInternalLinks(m.text) : [];
  const seen = new Set();
  const all = [];
  for (const l of [...structured, ...fromText]) {
    if (l.to && !seen.has(l.to)) {
      seen.add(l.to);
      all.push(l);
    }
  }
  return all;
}

async function downloadExport(messageId, fmt) {
  const token = getAccessToken();
  const res = await fetch(`${getApiBaseUrl()}/api/assistant/messages/${messageId}/export/${fmt}/`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error("Ошибка экспорта");
  const blob = await res.blob();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `assistant-report-${messageId}.${fmt}`;
  a.click();
  URL.revokeObjectURL(a.href);
}

export default function AssistantWidget() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const auth = useSelector(selectAuth);
  const assistant = useSelector(selectAssistant);
  const [input, setInput] = useState("");
  const [teaserVisible, setTeaserVisible] = useState(false);
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const listRef = useRef(null);

  const isAllowedRole = hasAnyGroup(auth.user, [
    GROUPS.DIRECTOR,
    GROUPS.DIRECTOR_ASSISTANT,
    GROUPS.MANAGER,
    GROUPS.WAREHOUSE_WORKER,
  ]);

  // Resolve which session to resume (server-side, per-user chat history) before ever
  // opening a socket — otherwise every reopen/page reload would start a blank new session.
  useEffect(() => {
    if (!ASSISTANT_ENABLED || !assistant.isOpen || !isAllowedRole) return;
    if (assistant.sessionResolved) return;
    dispatch(resumeLatestSession());
  }, [assistant.isOpen, assistant.sessionResolved, isAllowedRole, dispatch]);

  useEffect(() => {
    if (!ASSISTANT_ENABLED || !assistant.isOpen || !isAllowedRole || !assistant.sessionResolved) return;
    if (socketHandle) return;
    socketHandle = connectAssistantSocket(assistant.sessionId, {
      dispatch,
      actions: assistantActions,
      onOpen: (sessionId) => dispatch(fetchAssistantHistory(sessionId)),
    });
    return () => {
      if (socketHandle) {
        socketHandle.close();
        socketHandle = null;
      }
    };
    // assistant.sessionId в зависимостях нужен для «Очистить историю»: после удаления
    // сессия меняется на 0 (новая), а sessionResolved остаётся true, поэтому без этого
    // эффект бы не перезапустился и сокет остался бы висеть на удалённой сессии.
    // Штатный старт даёт максимум одно лишнее переподключение (0 → реальный id из кадра
    // connected), после чего значение стабильно.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assistant.isOpen, assistant.sessionResolved, assistant.sessionId, isAllowedRole]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [assistant.messages]);

  // Читаем через безопасные значения: отсутствие поля не должно ронять всю панель.
  const suggestions = assistant.suggestions || {};
  const suggestionItems = suggestions.items || [];
  const teaserUserId = auth.user?.id;
  const teaserText = suggestions.teaser?.text;

  // Подсказки зависят от роли и страницы: тянем при открытии панели и при навигации.
  // Тизеру они нужны до открытия панели, поэтому «панель закрыта» здесь не блокирует запрос.
  useEffect(() => {
    if (!ASSISTANT_ENABLED || !isAllowedRole) return;
    if (suggestions.loadedPath === pathname) return;
    // Панель закрыта и тизер за сегодня отработал — подсказки сейчас никому не нужны.
    if (!assistant.isOpen && isTeaserDismissedToday(teaserUserId)) return;
    dispatch(fetchAssistantSuggestions(pathname));
  }, [pathname, isAllowedRole, assistant.isOpen, suggestions.loadedPath, teaserUserId, dispatch]);

  useEffect(() => {
    if (!ASSISTANT_ENABLED || !isAllowedRole || !teaserText) return;
    if (isTeaserDismissedToday(teaserUserId)) return;
    // Пользователь и так открыл ассистента — рекламировать нечего.
    if (assistant.isOpen) {
      dismissTeaserForToday(teaserUserId);
      return;
    }

    const showTimer = setTimeout(() => setTeaserVisible(true), TEASER_DELAY_MS);
    // Автоскрытие тоже засчитываем как показ за сегодня, иначе тизер возвращался бы
    // при каждой навигации и превратился бы в мигающую рекламу.
    const hideTimer = setTimeout(() => {
      setTeaserVisible(false);
      dismissTeaserForToday(teaserUserId);
    }, TEASER_DELAY_MS + TEASER_AUTO_HIDE_MS);

    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
  }, [isAllowedRole, teaserText, teaserUserId, assistant.isOpen]);

  const dismissTeaser = () => {
    setTeaserVisible(false);
    dismissTeaserForToday(teaserUserId);
  };

  const askFromTeaser = () => {
    dismissTeaser();
    dispatch(pendingQuestionQueued(teaserText));
    if (!assistant.isOpen) dispatch(toggleOpen());
  };

  // Сообщения читаем через ref: если положить assistant.messages в зависимости эффекта ниже,
  // интервал пересоздаётся на КАЖДЫЙ чанк (массив каждый раз новый), и недотикавший интервал
  // каждый раз теряется — печать выходит медленнее и дёрганее номинальной.
  const messagesRef = useRef(assistant.messages);
  useEffect(() => {
    messagesRef.current = assistant.messages;
  }, [assistant.messages]);

  useEffect(() => {
    const timer = setInterval(() => {
      const messages = messagesRef.current;
      const last = messages[messages.length - 1];
      const pending = last && last.pendingText;
      if (pending && pending.length > 0) {
        // Размер порции зависит от глубины буфера: короткие ответы печатаются в прежнем
        // темпе, длинные не заставляют ждать 15 секунд.
        dispatch(revealNextChars(revealChunkSize(pending.length)));
      }
    }, REVEAL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [dispatch]);

  // Чипы отрисованы сразу, а сокет поднимается асинхронно после resumeLatestSession —
  // без проверки статуса ранний клик молча пропал бы. historyStatus проверяем отдельно:
  // fetchAssistantHistory уходит в тот же момент, когда сокет становится "open" (см. onOpen
  // в AssistantWidget), и если отправить сообщение до её ответа, её (обычно пустой) результат
  // может прийти уже во время стрима и молча оборвать печатающийся ответ.
  const canSend = assistant.connectionStatus === "open" && assistant.historyStatus !== "loading";
  const sendText = useCallback(
    (raw) => {
      const text = String(raw || "").trim();
      if (!text || !socketHandle || !canSend) return false;
      dispatch(userMessageSent(text));
      socketHandle.send(text);
      return true;
    },
    [canSend, dispatch]
  );

  const onSend = () => {
    if (sendText(input)) {
      setInput("");
      dispatch(setDraft(""));
    }
  };

  // Очистка запрещена во время стрима: закрыть сокет, пока рабочий поток ещё пишет
  // ChatMessage, значит каскадно снести наполовину записанный ответ.
  const isStreaming = assistant.messages.some((m) => m.isStreaming);
  const canClear = !clearing && !isStreaming && assistant.messages.length > 0;

  const doClearHistory = useCallback(async () => {
    setConfirmClearOpen(false);
    setClearing(true);
    // Порядок важен: сокет закрываем ДО запроса, иначе незавершённый turn успеет записать
    // сообщение в сессию, которую мы прямо сейчас удаляем.
    if (socketHandle) {
      socketHandle.close();
      socketHandle = null;
    }
    try {
      // sessionId 0/null — на сервере ещё ничего не сохранено, удалять нечего.
      if (assistant.sessionId) {
        await dispatch(deleteAssistantSession(assistant.sessionId)).unwrap();
      }
      dispatch(historyCleared());
    } catch {
      // Текст ошибки уже положен в state.error обработчиком rejected.
    } finally {
      setClearing(false);
    }
  }, [dispatch, assistant.sessionId]);

  // Вопрос из тизера уходит, как только сокет открылся.
  useEffect(() => {
    if (!assistant.pendingQuestion) return;
    if (sendText(assistant.pendingQuestion)) dispatch(pendingQuestionCleared());
  }, [assistant.pendingQuestion, sendText, dispatch]);

  if (!ASSISTANT_ENABLED) return null;

  return (
    <>
      <button type="button" className="assistant-fab" onClick={() => dispatch(toggleOpen())} title="Ассистент" aria-label="Ассистент">
        {ICONS.assistant}
      </button>

      {teaserVisible && !assistant.isOpen && teaserText && (
        <AssistantTeaser text={teaserText} onAsk={askFromTeaser} onDismiss={dismissTeaser} />
      )}

      {assistant.isOpen && (
        <div className="assistant-panel" style={assistant.panelSize === "full" ? styles.panelFull : styles.panelHalf}>
          <div style={styles.header}>
            <span>Ассистент</span>
            <div style={styles.headerActions}>
              <button
                type="button"
                style={canClear ? styles.iconBtn : { ...styles.iconBtn, ...styles.chipDisabled }}
                disabled={!canClear}
                onClick={() => setConfirmClearOpen(true)}
                title="Очистить историю"
              >
                {ICONS.trash}
              </button>
              <button
                type="button"
                className="assistant-size-toggle"
                style={styles.iconBtn}
                onClick={() => dispatch(togglePanelSize())}
                title={assistant.panelSize === "full" ? "Свернуть до половины экрана" : "На весь экран"}
              >
                {assistant.panelSize === "full" ? ICONS.collapse : ICONS.expand}
              </button>
              <button type="button" style={styles.iconBtn} onClick={() => dispatch(toggleOpen())} title="Закрыть">
                {ICONS.close}
              </button>
            </div>
          </div>

          {!isAllowedRole ? (
            <div style={styles.notice}>Ассистент пока недоступен для вашей роли.</div>
          ) : (
            <AssistantErrorBoundary onClose={() => dispatch(toggleOpen())}>
              <div ref={listRef} style={styles.messages}>
                {assistant.messages.length === 0 && (
                  <div style={styles.emptyState}>
                    <div style={styles.greeting}>
                      {suggestions.greeting ||
                        "Спросите про отчёты, прибыль, ревизии склада или кто должен деньги."}
                    </div>
                    {suggestions.status === "loading" && <div style={styles.notice}>Подбираю подсказки…</div>}
                    {assistant.historyStatus === "loading" && (
                      <div style={styles.notice}>Загружаю историю…</div>
                    )}
                    <div style={styles.chipRow}>
                      {suggestionItems.map((s) => (
                        <button
                          key={s.key}
                          type="button"
                          style={canSend ? styles.chip : { ...styles.chip, ...styles.chipDisabled }}
                          disabled={!canSend}
                          onClick={() => sendText(s.text)}
                        >
                          {s.text}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {assistant.messages.map((m) => (
                  <div key={m.id} style={m.role === "user" ? styles.userBubble : styles.assistantBubble}>
                    <div>
                      {m.role === "assistant" ? formatAssistantText(m.text, navigate) : m.text}
                      {m.isStreaming && <span style={styles.cursor}>▍</span>}
                    </div>
                    {m.chartSpec && <AssistantChart spec={m.chartSpec} />}
                    {m.toolResult?.customers && <AssistantDebtTable customers={m.toolResult.customers} />}
                    {buildMessageLinks(m, auth.user).map((link) => (
                      <AssistantLinkCard key={link.to} link={link} onOpen={navigate} />
                    ))}
                    {(m.chartSpec || m.toolResult) && m.id > 0 && (
                      <div style={styles.exportRow}>
                        <button type="button" style={styles.exportBtn} onClick={() => downloadExport(m.id, "xlsx")}>
                          {ICONS.download} Excel
                        </button>
                        <button type="button" style={styles.exportBtn} onClick={() => downloadExport(m.id, "pdf")}>
                          {ICONS.download} PDF
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {assistant.error && (
                <div style={styles.errorBanner} onClick={() => dispatch(clearError())}>
                  {assistant.error}
                </div>
              )}

              <div style={styles.inputRow}>
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && onSend()}
                  placeholder="Спросите ассистента…"
                  style={styles.input}
                  disabled={assistant.connectionStatus === "error"}
                />
                <button type="button" style={styles.sendBtn} onClick={onSend} aria-label="Отправить">
                  {ICONS.send}
                </button>
              </div>
              {assistant.connectionStatus === "error" && (
                <div style={styles.notice}>Ассистент офлайн. Обновите страницу.</div>
              )}
            </AssistantErrorBoundary>
          )}

          {/* Внутри панели, а не рядом: у .cm-overlay и panelBase одинаковый z-index (1000),
              поэтому поверх панели модалку выводит порядок в DOM. */}
          <ConfirmModal
            open={confirmClearOpen}
            title="Очистить историю?"
            message="Вся переписка с ассистентом в этом чате будет удалена без возможности восстановления."
            confirmLabel="Очистить"
            cancelLabel="Отмена"
            danger
            onConfirm={doClearHistory}
            onCancel={() => setConfirmClearOpen(false)}
          />
        </div>
      )}
    </>
  );
}

const styles = {
  panelBase: {
    position: "fixed",
    top: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    flexDirection: "column",
    background: "var(--c-card)",
    borderLeft: "1px solid var(--c-border-light)",
    boxShadow: "-24px 0 64px rgba(0,0,0,0.4)",
    zIndex: 1000,
  },
  get panelHalf() {
    return { ...this.panelBase, width: "min(480px, 50vw)" };
  },
  get panelFull() {
    return { ...this.panelBase, width: "100vw" };
  },
  header: {
    padding: "12px 14px 12px 18px",
    borderBottom: "1px solid var(--c-border)",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontWeight: 700,
    fontSize: 16,
    color: "var(--c-text)",
    background: "var(--c-card)",
    flexShrink: 0,
  },
  headerActions: { display: "flex", gap: 10, alignItems: "center" },
  iconBtn: {
    width: 30,
    height: 30,
    display: "grid",
    placeItems: "center",
    border: "none",
    borderRadius: 99,
    background: "transparent",
    color: "var(--c-text-muted)",
    cursor: "pointer",
  },
  messages: { flex: 1, overflowY: "auto", padding: 12, display: "flex", flexDirection: "column", gap: 8 },
  notice: { fontSize: 14, color: "var(--c-text-sub)", padding: 8 },
  emptyState: { display: "flex", flexDirection: "column", gap: 10, padding: 8 },
  greeting: { fontSize: 15, color: "var(--c-text)", lineHeight: 1.45 },
  chipRow: { display: "flex", flexWrap: "wrap", gap: 6 },
  chip: {
    textAlign: "left",
    fontSize: 14,
    padding: "6px 10px",
    borderRadius: 99,
    border: "1px solid var(--c-border)",
    background: "var(--c-input)",
    color: "var(--c-text)",
    cursor: "pointer",
  },
  chipDisabled: { opacity: 0.5, cursor: "default" },
  userBubble: {
    alignSelf: "flex-end",
    // Не жёлтый: жёлтый закреплён за брендом и главным действием, а не
    // за «моими репликами». Графит читается как «сказал я».
    background: "var(--c-btn)",
    color: "var(--c-btn-text)",
    borderRadius: "16px 16px 4px 16px",
    padding: "10px 14px",
    fontSize: 15,
    lineHeight: 1.45,
    maxWidth: "82%",
    boxShadow: "var(--c-shadow-sm)",
  },
  assistantBubble: {
    alignSelf: "flex-start",
    background: "var(--c-card)",
    color: "var(--c-text)",
    border: "1px solid var(--c-border)",
    // Срезанный угол у «своей» стороны — направление реплики читается
    // без аватаров и подписей.
    borderRadius: "16px 16px 16px 4px",
    padding: "10px 14px",
    fontSize: 15,
    lineHeight: 1.5,
    maxWidth: "88%",
  },
  cursor: { opacity: 0.6 },
  exportRow: { display: "flex", gap: 8, marginTop: 6 },
  exportBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: 13,
    fontWeight: 600,
    padding: "6px 12px",
    borderRadius: 99,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    color: "var(--c-text-sub)",
    cursor: "pointer",
  },
  linkRow: { display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 },
  linkBtn: {
    fontSize: 14,
    padding: "6px 10px",
    borderRadius: 99,
    border: "1px solid var(--c-accent)",
    background: "var(--c-btn)",
    color: "var(--c-btn-text)",
    cursor: "pointer",
    fontWeight: 600,
    textAlign: "left",
  },
  errorBanner: {
    fontSize: 14,
    color: "var(--c-danger)",
    padding: "6px 12px",
    cursor: "pointer",
    borderTop: "1px solid var(--c-border)",
  },
  inputRow: {
    display: "flex",
    gap: 8,
    padding: 12,
    borderTop: "1px solid var(--c-border)",
    background: "var(--c-card)",
  },
  input: {
    flex: 1,
    padding: "10px 14px",
    borderRadius: 99,
    border: "1px solid var(--c-border)",
    background: "var(--c-input)",
    color: "var(--c-text)",
    fontSize: 15,
    outline: "none",
  },
  sendBtn: {
    width: 40,
    height: 40,
    flexShrink: 0,
    display: "grid",
    placeItems: "center",
    borderRadius: "50%",
    border: "none",
    background: "var(--c-btn)",
    color: "var(--c-btn-text)",
    cursor: "pointer",
  },
};
